import { useEffect, useRef, useState } from "react";
import { Search, Loader2, X, Phone, ArrowRight } from "lucide-react";
import ReactMarkdown from "react-markdown";
import { useNavigate } from "@/lib/router-compat";
import { getOrCreateChatSessionKey } from "@/lib/chatSessionKey";

const CHAT_URL = `${import.meta.env["VITE_SUPABASE_URL"]}/functions/v1/chat`;

type Msg = { role: "user" | "assistant"; content: string };


/**
 * Site-search-style input powered by the same assistant as the chat widget.
 * Answers render inline as search results; no chat bubbles or assistant name.
 */
export default function SmartSearchBar() {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [history, setHistory] = useState<Msg[]>([]);
  const [answer, setAnswer] = useState("");
  const [asked, setAsked] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const abortRef = useRef<AbortController | null>(null);

  const run = async (q: string) => {
    const text = q.trim();
    if (!text || loading) return;
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setAsked(text);
    setAnswer("");
    setError("");
    setLoading(true);
    const msgs: Msg[] = [...history, { role: "user", content: text }];
    const hint =
      "[Site search box: reply as a short search result (2-4 sentences or a brief list) with helpful page links. Do not introduce yourself, use a name, or ask for the visitor's name.]\n";
    const sent = msgs.map((m, idx) =>
      idx === msgs.length - 1 ? { ...m, content: hint + m.content } : m,
    );
    let acc = "";
    try {
      const resp = await fetch(CHAT_URL, {
        method: "POST",
        signal: ctrl.signal,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${import.meta.env["VITE_SUPABASE_PUBLISHABLE_KEY"]}`,
        },
        body: JSON.stringify({
          messages: sent,
          sessionKey: `${getOrCreateChatSessionKey()}-search`,
        }),
      });
      if (!resp.ok || !resp.body) {
        const d = await resp.json().catch(() => ({}));
        throw new Error(d.error || "Search is unavailable right now. Please try again.");
      }
      if ((resp.headers.get("content-type") || "").includes("application/json")) {
        setLoading(false);
        return;
      }
      const reader = resp.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        let i: number;
        while ((i = buf.indexOf("\n")) !== -1) {
          const line = buf.slice(0, i).replace(/\r$/, "");
          buf = buf.slice(i + 1);
          if (!line.startsWith("data: ")) continue;
          const json = line.slice(6).trim();
          if (json === "[DONE]") continue;
          try {
            const c = JSON.parse(json).choices?.[0]?.delta?.content;
            if (c) {
              acc += c;
              setAnswer(acc);
            }
          } catch {
            /* partial line */
          }
        }
      }
      setHistory([...msgs, { role: "assistant" as const, content: acc }].slice(-8));
    } catch (e) {
      if ((e as Error).name !== "AbortError") setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const clear = () => {
    abortRef.current?.abort();
    setAnswer("");
    setAsked("");
    setError("");
    setQuery("");
    setLoading(false);
  };

  useEffect(() => {
    if (!asked) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && clear();
    window.addEventListener("keydown", onKey);
    // Lock page scroll while the panel is open, keeping the scrollbar gutter
    // so the header/menu doesn't jump or jitter when the window is resized.
    const html = document.documentElement;
    const prevOverflow = html.style.overflow;
    const prevGutter = html.style.scrollbarGutter;
    html.style.scrollbarGutter = "stable";
    html.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      html.style.overflow = prevOverflow;
      html.style.scrollbarGutter = prevGutter;
    };
  }, [asked]);

  return (
    <section className="smart-search-bar relative border-b border-border bg-muted/60">
      <div className="container py-5">
        <form
          role="search"
          onSubmit={(e) => {
            e.preventDefault();
            run(query);
          }}
          className="relative z-40 mx-auto max-w-3xl"
        >
          <label htmlFor="smart-search" className="sr-only">
            Search Rex & Sons RVs
          </label>
          <Search className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
          <input
            id="smart-search"
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search RVs, floorplans, service, financing…"
            autoComplete="off"
            className="h-14 w-full rounded-full border border-input bg-background pl-12 pr-32 text-base text-foreground shadow-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/30"
          />
          <button
            type="submit"
            disabled={loading || !query.trim()}
            className="absolute right-2 top-1/2 inline-flex h-10 -translate-y-1/2 items-center gap-2 rounded-full bg-primary px-5 font-heading text-sm font-semibold uppercase tracking-wide text-primary-foreground disabled:opacity-60"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Search"}
          </button>
        </form>


        {asked && (
          <>
          <div aria-hidden onClick={clear} className="fixed inset-0 z-30 bg-foreground/30 animate-in fade-in" />
          <div
            role="dialog"
            aria-label="Search results"
            aria-live="polite"
            className="absolute left-1/2 top-full z-40 mt-2 max-h-[70vh] w-[min(48rem,calc(100%-2rem))] -translate-x-1/2 overflow-y-auto rounded-lg border border-border bg-background p-5 shadow-2xl animate-in fade-in slide-in-from-top-2"
          >
            <div className="mb-3 flex items-start justify-between gap-3">
              <p className="text-sm text-muted-foreground">
                Results for <span className="font-semibold text-foreground">“{asked}”</span>
              </p>
              <button type="button" onClick={clear} aria-label="Clear search" className="text-muted-foreground hover:text-foreground">
                <X className="h-4 w-4" />
              </button>
            </div>
            {error ? (
              <p className="text-sm text-destructive">{error}</p>
            ) : !answer && loading ? (
              <div className="space-y-2">
                <div className="h-3 w-3/4 animate-pulse rounded bg-muted" />
                <div className="h-3 w-2/3 animate-pulse rounded bg-muted" />
                <div className="h-3 w-1/2 animate-pulse rounded bg-muted" />
              </div>
            ) : (
              <div className="prose prose-sm max-w-none text-foreground prose-a:text-primary">
                <ReactMarkdown
                  components={{
                    a: ({ href, children }) => (
                      <a
                        href={href}
                        onClick={(e) => {
                          if (href?.startsWith("/")) {
                            e.preventDefault();
                            navigate(href);
                          }
                        }}
                        target={href?.startsWith("/") ? undefined : "_blank"}
                        rel="noopener noreferrer"
                        className="font-semibold text-primary underline underline-offset-2"
                      >
                        {children}
                      </a>
                    ),
                  }}
                >
                  {answer}
                </ReactMarkdown>
              </div>
            )}
            {!loading && answer && (
              <div className="mt-4 flex flex-wrap gap-3 border-t border-border pt-4 text-sm">
                <a href="/rv-search" onClick={(e) => { e.preventDefault(); navigate("/rv-search"); }} className="inline-flex items-center gap-1 font-semibold text-primary">
                  Browse inventory <ArrowRight className="h-4 w-4" />
                </a>
                <a href="tel:9107932894" className="inline-flex items-center gap-1 font-semibold text-primary">
                  <Phone className="h-4 w-4" /> Call (910) 793-2894
                </a>
              </div>
            )}
          </div>
          </>
        )}
      </div>
    </section>
  );
}
